import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  FileEdit,
  Loader2,
  Paperclip,
  X,
  Upload,
  Target,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { timeTrackingAPI } from '../../lib/api';
import { formatDate, formatTime, isoToLocalHHMM } from './utils';

// Pause + HH:MM -> Netto-Minuten (lokale Interpretation)
const calcNetMinutes = (hhmmIn, hhmmOut, breakMinutes) => {
  if (!hhmmIn || !hhmmOut) return null;
  const [hi, mi] = hhmmIn.split(':').map((v) => parseInt(v, 10));
  const [ho, mo] = hhmmOut.split(':').map((v) => parseInt(v, 10));
  if ([hi, mi, ho, mo].some((v) => Number.isNaN(v))) return null;
  let total = ho * 60 + mo - (hi * 60 + mi);
  if (total < 0) total += 24 * 60; // Uhrzeit ueber Mitternacht
  const pause = Math.max(0, parseInt(breakMinutes, 10) || 0);
  return Math.max(0, total - pause);
};

const minutesToHours = (minutes) => {
  if (minutes == null) return '–';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')} h`;
};

export const CorrectionDialog = ({
  open,
  onOpenChange,
  selectedEntry,
  correctionData,
  setCorrectionData,
  onSubmit,
  submitting,
  targetHours,
}) => {
  const originalIn = selectedEntry?.clock_in ? isoToLocalHHMM(selectedEntry.clock_in) : '';
  const originalOut = selectedEntry?.clock_out ? isoToLocalHHMM(selectedEntry.clock_out) : '';
  const originalBreak = selectedEntry?.total_break_minutes ?? 0;
  const originalNet = selectedEntry?.net_work_minutes ?? calcNetMinutes(originalIn, originalOut, originalBreak);
  const isNew = !selectedEntry;
  const today = new Date().toISOString().split('T')[0];

  // Self-Service: geplanten Einsatz laden und (bei leeren Feldern) vorbefüllen
  const [planned, setPlanned] = useState(null);
  const [plannedAutofilled, setPlannedAutofilled] = useState(false);
  useEffect(() => {
    if (!open || !isNew || !correctionData.date) { setPlanned(null); setPlannedAutofilled(false); return undefined; }
    let active = true;
    const t = setTimeout(() => {
      timeTrackingAPI.getMyPlannedShift(correctionData.date)
        .then((res) => {
          if (!active) return;
          const p = res.data?.found ? res.data : null;
          setPlanned(p);
          if (p && p.start_time && p.end_time) {
            setCorrectionData((prev) => {
              if (prev.clock_in || prev.clock_out) return prev;
              setPlannedAutofilled(true);
              return {
                ...prev,
                clock_in: p.start_time,
                clock_out: p.end_time,
                break_minutes: String(p.pause_minutes || 0),
              };
            });
          }
        })
        .catch(() => { if (active) setPlanned(null); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNew, correctionData.date]);

  const applyPlannedSelf = () => {
    if (!planned) return;
    setCorrectionData((prev) => ({
      ...prev,
      clock_in: planned.start_time || prev.clock_in,
      clock_out: planned.end_time || prev.clock_out,
      break_minutes: String(planned.pause_minutes || 0),
    }));
    setPlannedAutofilled(true);
  };

  const newNet = useMemo(
    () => calcNetMinutes(correctionData.clock_in, correctionData.clock_out, correctionData.break_minutes),
    [correctionData.clock_in, correctionData.clock_out, correctionData.break_minutes],
  );

  const dailyTargetHours = targetHours?.daily_hours;
  const targetMinutes = dailyTargetHours ? Math.round(dailyTargetHours * 60) : null;
  const diffMinutes = newNet != null && targetMinutes != null ? newNet - targetMinutes : null;

  const dateChanged = selectedEntry && correctionData.date && correctionData.date !== selectedEntry.date;
  const clockInChanged = correctionData.clock_in !== originalIn;
  const clockOutChanged = correctionData.clock_out !== originalOut;
  const breakChanged = String(correctionData.break_minutes ?? '') !== String(originalBreak ?? '');
  const anyChange = dateChanged || clockInChanged || clockOutChanged || breakChanged;

  const canSubmit = isNew
    ? Boolean(correctionData.date && correctionData.clock_in && correctionData.clock_out && correctionData.reason.trim())
    : Boolean(anyChange && correctionData.reason.trim());

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10 MB)');
      return;
    }
    setCorrectionData((prev) => ({ ...prev, attachmentUploading: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/time-tracking/corrections/attachments', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCorrectionData((prev) => ({
        ...prev,
        attachment: {
          filename: res.data.filename,
          original_name: res.data.original_name || file.name,
          size: res.data.size || file.size,
        },
        attachmentUploading: false,
      }));
      toast.success('Anhang hochgeladen');
    } catch (err) {
      setCorrectionData((prev) => ({ ...prev, attachmentUploading: false }));
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      // reset input so same file can be re-picked
      e.target.value = '';
    }
  };

  const removeAttachment = () => {
    setCorrectionData((prev) => ({ ...prev, attachment: null }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileEdit className="h-5 w-5" />
            {isNew ? 'Fehlenden Tag nachtragen' : 'Korrektur beantragen'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
            {isNew ? (
              <div className="rounded-lg border border-amber-300/70 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200" data-testid="correction-new-day-note">
                Für diesen Tag existiert noch keine Buchung. Trage Datum, Kommen- und Gehen-Zeit ein.
                Der Antrag wird zur <span className="font-semibold">Genehmigung</span> weitergeleitet.
              </div>
            ) : (
            /* Vorher / Nachher Vergleich */
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border/60 bg-secondary/40 p-3" data-testid="correction-original-card">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Alt</p>
                <p className="text-xs text-muted-foreground">{formatDate(selectedEntry.date)}</p>
                <p className="font-mono text-sm mt-1">{formatTime(selectedEntry.clock_in)} – {formatTime(selectedEntry.clock_out)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pause: {originalBreak} min · Netto: {minutesToHours(originalNet)}
                </p>
              </div>
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="correction-new-card">
                <p className="text-[11px] uppercase tracking-wide text-primary font-semibold mb-1 flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" />Neu
                </p>
                <p className="text-xs text-muted-foreground">{correctionData.date ? formatDate(correctionData.date) : '—'}</p>
                <p className="font-mono text-sm mt-1 text-primary">
                  {correctionData.clock_in || '--:--'} – {correctionData.clock_out || '--:--'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pause: {correctionData.break_minutes || 0} min · Netto: <span className="font-semibold text-foreground">{minutesToHours(newNet)}</span>
                </p>
              </div>
            </div>
            )}

            {isNew && planned && (
              <div className="flex items-center justify-between rounded-md border border-sky-300/60 bg-sky-50 dark:bg-sky-950/30 px-2.5 py-1.5 text-xs" data-testid="correction-planned-shift">
                <span className="text-sky-800 dark:text-sky-300">
                  {plannedAutofilled ? 'Aus Dienstplan übernommen: ' : 'Geplanter Einsatz: '}
                  <span className="font-semibold">{planned.start_time}–{planned.end_time}</span>
                  {planned.pause_minutes ? ` · Pause ${planned.pause_minutes} Min` : ''}
                  {planned.massnahme_name ? ` · ${planned.massnahme_name}` : ''}
                </span>
                <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={applyPlannedSelf} data-testid="correction-apply-planned">
                  {plannedAutofilled ? 'Erneut übernehmen' : 'Übernehmen'}
                </Button>
              </div>
            )}

            {/* Soll-Arbeitszeit / Diff */}
            {targetMinutes != null && (
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-background p-2.5 text-xs" data-testid="correction-target-hours">
                <Target className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Tages-Soll:</span>
                <span className="font-medium">{minutesToHours(targetMinutes)}</span>
                {diffMinutes != null && (
                  <Badge
                    variant={diffMinutes >= 0 ? 'secondary' : 'destructive'}
                    className={diffMinutes >= 0 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : ''}
                  >
                    {diffMinutes >= 0 ? '+' : '−'}{minutesToHours(Math.abs(diffMinutes))}
                  </Badge>
                )}
              </div>
            )}

            {/* Datum */}
            <div className="space-y-2">
              <Label htmlFor="correction-date">Datum</Label>
              <Input
                id="correction-date"
                data-testid="correction-input-date"
                type="date"
                max={isNew ? today : undefined}
                value={correctionData.date || ''}
                onChange={(e) => setCorrectionData((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>

            {/* Kommen / Gehen / Pause */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="correction-clock-in">Kommen</Label>
                <Input
                  id="correction-clock-in"
                  data-testid="correction-input-clock-in"
                  type="time"
                  value={correctionData.clock_in}
                  onChange={(e) => setCorrectionData((prev) => ({ ...prev, clock_in: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="correction-clock-out">Gehen</Label>
                <Input
                  id="correction-clock-out"
                  data-testid="correction-input-clock-out"
                  type="time"
                  value={correctionData.clock_out}
                  onChange={(e) => setCorrectionData((prev) => ({ ...prev, clock_out: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="correction-break">Pause (min)</Label>
                <Input
                  id="correction-break"
                  data-testid="correction-input-break"
                  type="number"
                  min="0"
                  step="5"
                  value={correctionData.break_minutes}
                  onChange={(e) => setCorrectionData((prev) => ({ ...prev, break_minutes: e.target.value }))}
                />
              </div>
            </div>

            {/* Begründung */}
            <div className="space-y-2">
              <Label htmlFor="correction-reason">Begründung *</Label>
              <Textarea
                id="correction-reason"
                data-testid="correction-input-reason"
                value={correctionData.reason}
                onChange={(e) => setCorrectionData((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Bitte begründen Sie die Korrektur..."
                rows={3}
              />
            </div>

            {/* Anhang */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" /> Nachweis (optional)
              </Label>
              {correctionData.attachment ? (
                <div className="flex items-center gap-2 rounded-md border border-border/60 bg-secondary/30 px-3 py-2 text-sm" data-testid="correction-attachment-preview">
                  <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate flex-1" title={correctionData.attachment.original_name}>
                    {correctionData.attachment.original_name}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={removeAttachment}
                    aria-label="Anhang entfernen"
                    data-testid="correction-attachment-remove"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="correction-attachment-file"
                  className="flex items-center gap-2 rounded-md border border-dashed border-border/70 bg-background px-3 py-2 text-sm text-muted-foreground cursor-pointer hover:bg-secondary/40 transition-colors"
                  data-testid="correction-attachment-drop"
                >
                  {correctionData.attachmentUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  <span>{correctionData.attachmentUploading ? 'Wird hochgeladen…' : 'Datei auswählen (PDF / JPG / PNG, max. 10 MB)'}</span>
                  <input
                    id="correction-attachment-file"
                    data-testid="correction-attachment-input"
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    disabled={correctionData.attachmentUploading}
                  />
                </label>
              )}
            </div>
          </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="correction-cancel-btn">
            Abbrechen
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || correctionData.attachmentUploading || !canSubmit}
            data-testid="correction-submit-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <FileEdit className="h-4 w-4 mr-2" />
            )}
            Antrag einreichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
